import React from "react";
import { Phone, Stethoscope, AlertTriangle } from "lucide-react";
import { PetAvatar } from "@/components/PetAvatar";
import { CARE_TYPES, DAYS, VET_LINE } from "@/lib/constants";
import { fmtTime, assetUrl } from "@/lib/api";
import { ITEM_CATS } from "@/components/PetItems";

const freqText = (s) => {
  if (s.frequency === "weekly") return (s.days_of_week || []).map((d) => DAYS[d]).join(", ");
  if (s.frequency === "monthly") return `Monthly on day ${s.day_of_month}`;
  if (s.frequency === "interval") return `Every ${s.interval_days} days`;
  return "Daily";
};

const age = (b) => {
  if (!b) return null;
  const y = (Date.now() - new Date(b).getTime()) / (365.25 * 864e5);
  return y < 1 ? `${Math.max(1, Math.round(y * 12))} mo` : `${Math.floor(y)} yr`;
};

function Row({ label, value }) {
  if (!value) return null;
  return <div className="text-sm"><span className="font-semibold text-slate-700">{label}: </span><span className="text-slate-600">{value}</span></div>;
}

export function GuidePet({ pet, household }) {
  return (
    <section data-testid={`guide-pet-${pet.id}`} className="pl-card print-page p-5 sm:p-7 mb-5">
      <div className="flex items-center gap-4 mb-4">
        <PetAvatar pet={pet} size={84} ring />
        <div>
          <h2 className="font-heading text-3xl font-bold">{pet.name}</h2>
          <p className="text-slate-500 capitalize">{[pet.species, pet.breed, age(pet.birthday), pet.weight].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      {pet.allergies && <p className="mb-3 rounded-xl bg-[#FDF2F0] text-[#C2410C] px-3 py-2 text-sm font-semibold flex gap-2"><AlertTriangle className="h-4 w-4 mt-0.5" />Allergies: {pet.allergies}</p>}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
        <Row label="Food" value={pet.food} /><Row label="Medications" value={pet.medications_note} />
        <Row label="Conditions" value={pet.conditions} /><Row label="Microchip" value={pet.microchip} />
      </div>
      {pet.quirks && <p className="text-sm text-slate-600 mb-4 italic">"{pet.quirks}"</p>}
      <h3 className="font-heading font-semibold text-lg mb-2">Daily routine</h3>
      <div className="divide-y divide-[#EFECE6] mb-4">
        {pet.schedules.length === 0 && <p className="text-sm text-slate-500 py-2">No schedule set yet.</p>}
        {pet.schedules.map((s) => {
          const t = CARE_TYPES[s.type] || CARE_TYPES.other;
          return (
            <div key={s.id} className="py-2 flex gap-3">
              <t.icon className="h-4 w-4 mt-1 shrink-0" style={{ color: t.color }} />
              <div className="text-sm">
                <p className="font-semibold">{s.title}{s.amount ? ` · ${s.amount}` : ""}</p>
                <p className="text-slate-500">{freqText(s)} · {(s.times?.length ? s.times : ["anytime"]).map(fmtTime).join(", ")}</p>
                {s.instructions && <p className="text-slate-600">{s.instructions}</p>}
              </div>
            </div>
          );
        })}
      </div>
      {pet.favorites?.length > 0 && (
        <>
          <h3 className="font-heading font-semibold text-lg mb-2">Foods, treats & favorites</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4" data-testid={`guide-favorites-${pet.id}`}>
            {pet.favorites.map((i) => {
              const c = ITEM_CATS[i.category] || ITEM_CATS.other;
              return (
                <div key={i.id} className="rounded-xl bg-[#FAF7F2] overflow-hidden">
                  {i.photo_url ? <img src={assetUrl(i.photo_url)} alt={i.name} className="aspect-square w-full object-cover" loading="lazy" />
                    : <div className="aspect-square grid place-items-center"><c.icon className="h-7 w-7" style={{ color: c.color }} /></div>}
                  <p className="text-xs font-semibold px-2 pt-1.5">{i.name}</p>
                  <p className="text-[10px] px-2 pb-1.5 text-slate-500">{c.label}{i.notes ? ` · ${i.notes}` : ""}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-[#ECFDF5] p-3 text-sm">
          <p className="font-semibold flex items-center gap-1.5"><Stethoscope className="h-4 w-4" />Vet</p>
          <p>{pet.vet_name || household.vet_name || "Not set"} {household.vet_clinic ? `· ${household.vet_clinic}` : ""}</p>
          {(pet.vet_phone || household.vet_phone) && <a href={`tel:${pet.vet_phone || household.vet_phone}`} className="font-semibold text-[#047857]">{pet.vet_phone || household.vet_phone}</a>}
          {household.vet_address && <p className="text-slate-500">{household.vet_address}</p>}
        </div>
        <div className="rounded-xl bg-[#FFF7E6] p-3 text-sm">
          <p className="font-semibold flex items-center gap-1.5"><Phone className="h-4 w-4" />Emergency contact</p>
          <p>{household.emergency_name || "Not set"}</p>
          {household.emergency_phone && <a href={`tel:${household.emergency_phone}`} className="font-semibold text-[#B45309]">{household.emergency_phone}</a>}
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-4">{VET_LINE}</p>
    </section>
  );
}

export function GuideSheet({ guide }) {
  return (
    <div data-testid="guide-sheet">
      {guide.household.house_notes && (
        <div className="pl-card p-5 mb-5"><h3 className="font-heading font-semibold text-lg mb-1">House notes</h3>
          <p className="text-sm text-slate-600 whitespace-pre-line">{guide.household.house_notes}</p></div>
      )}
      {guide.pets.map((p) => <GuidePet key={p.id} pet={p} household={guide.household} />)}
    </div>
  );
}
